import { useState, useMemo, useEffect } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { 
  X, 
  Search, 
  User, 
  Calendar,
  Users,
  UserX,
  MessageSquare,
  RefreshCw,
  Clock,
  Zap
} from 'lucide-react';
import { cn } from "@/lib/utils";
import { useInterviewCandidateCache, type InterviewCandidate } from '../../hooks/useInterviewCandidateCache';


interface CandidateSelectorProps {
  onSelect: (candidate: InterviewCandidate) => void;
  onCancel: () => void;
  projectId?: string;
}

// Note: Helper functions are now handled by the useInterviewCandidateCache hook

// Note: parseOriginalInterviewPlan function has been moved to useInterviewCandidateCache hook

// Note: parseInterviewPerspectives function has been moved to useInterviewCandidateCache hook

// Note: generateInterviewQuestions function has been moved to useInterviewCandidateCache hook

// Note: fetchAnalysisDataOptimized function has been moved to useInterviewCandidateCache hook

export function CandidateSelector({ onSelect, onCancel, projectId }: CandidateSelectorProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [cacheInfo, setCacheInfo] = useState<{
    hitRate: number;
    totalEntries: number;
    lastRefresh: Date | null;
    cacheSize: string;
  } | null>(null);

  // Use the specialized interview candidate cache hook
  const {
    candidates,
    isLoading,
    isFromCache,
    error,
    refreshCandidates,
    invalidateCache,
    getCacheInfo,
    warmCache
  } = useInterviewCandidateCache({
    projectId,
    enableAutoRefresh: true,
    refreshIntervalMinutes: 5,
    cacheTimeMinutes: 30,
    maxCandidates: 100
  });

  // Enhanced filtered candidates with better performance
  const filteredCandidates = useMemo(() => {
    if (!searchTerm.trim()) {
      return candidates;
    }
    
    const normalizedSearch = searchTerm.toLowerCase().trim();
    
    return candidates.filter(candidate => {
      // Quick early exit checks first
      if (candidate.name.toLowerCase().includes(normalizedSearch)) return true;
      if (candidate.firstName.toLowerCase().includes(normalizedSearch)) return true;
      if (candidate.lastName.toLowerCase().includes(normalizedSearch)) return true;
      if (candidate.position.toLowerCase().includes(normalizedSearch)) return true;
      
      // Check skills last as it's more expensive
      if (candidate.skills?.some(skill => skill.toLowerCase().includes(normalizedSearch))) return true;
      
      return false;
    });
  }, [searchTerm, candidates]);

  // Load cache info on mount and when candidates change
  useEffect(() => {
    if (candidates.length > 0) {
      getCacheInfo().then(info => {
        setCacheInfo(info);
        console.log('📊 Interview cache performance:', {
          hitRate: `${info.hitRate.toFixed(1)}%`,
          totalEntries: info.totalEntries,
          lastRefresh: info.lastRefresh?.toLocaleTimeString(),
          cacheSize: info.cacheSize
        });
      });
    }
  }, [candidates.length, getCacheInfo]);

  // Warm cache on component mount for better UX
  useEffect(() => {
    if (!isLoading && candidates.length === 0) {
      warmCache();
    }
  }, [isLoading, candidates.length, warmCache]);

  // Helper functions for UI styling
  const getVerdictColor = (verdict: string) => {
    switch (verdict) {
      case 'STRONG_FIT': return 'bg-green-50 text-green-700 border-green-200';
      case 'POTENTIAL_FIT': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'NEEDS_CONSIDERATION': return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 'NOT_RECOMMENDED': return 'bg-red-50 text-red-700 border-red-200';
      default: return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 8) return 'text-green-600';
    if (score >= 6) return 'text-yellow-600';
    if (score >= 4) return 'text-orange-600';
    return 'text-red-600';
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden border border-slate-200">
        {/* Enhanced Header */}
        <div className="bg-slate-50 border-b border-slate-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-slate-900 rounded-xl flex items-center justify-center">
                <Users className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900">Select Candidate</h2>
                <div className="flex items-center gap-4 mt-1">
                  <p className="text-slate-600">
                    {projectId 
                      ? "Choose from candidates analyzed for this project" 
                      : "Choose from all analyzed candidates"
                    }
                  </p>
                  {isFromCache && (
                    <Badge variant="outline" className="text-xs bg-green-50 border-green-200 text-green-700">
                      <Clock className="h-3 w-3 mr-1" />
                      Cached
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Enhanced action buttons */}
              <Button
                onClick={refreshCandidates}
                variant="outline"
                size="sm"
                className="rounded-lg border-slate-200 hover:bg-slate-100"
                disabled={isLoading}
                title="Refresh candidate list"
              >
                <RefreshCw className={cn("h-4 w-4", isLoading && "animate-spin")} />
              </Button>
              {process.env.NODE_ENV === 'development' && (
                <Button
                  onClick={invalidateCache}
                  variant="outline"
                  size="sm"
                  className="rounded-lg border-slate-200 hover:bg-slate-100 text-xs"
                  title="Clear cache (dev only)"
                >
                  <Zap className="h-4 w-4 mr-1" />
                  Clear Cache
                </Button>
              )}
              <Button
                onClick={onCancel}
                variant="outline"
                size="sm"
                className="rounded-lg border-slate-200 hover:bg-slate-100"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Enhanced Search with cache info */}
        <div className="p-6 border-b border-slate-200">
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search candidates by name, position, or skills..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 rounded-lg border-slate-200 focus:border-slate-400 focus:ring-slate-400"
              />
            </div>
            {/* Enhanced results info */}
            {!isLoading && (
              <div className="flex items-center justify-between text-sm text-slate-600">
                <span>
                  {filteredCandidates.length} of {candidates.length} candidates
                  {searchTerm && ` matching "${searchTerm}"`}
                </span>
                <div className="flex items-center gap-4 text-xs">
                  {cacheInfo?.lastRefresh && (
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      Last updated: {cacheInfo.lastRefresh.toLocaleTimeString()}
                    </span>
                  )}

                </div>
              </div>
            )}
          </div>
        </div>

        {/* Enhanced Content with better error handling */}
        <div className="overflow-y-auto max-h-[calc(90vh-220px)] p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="text-center space-y-4">
                <div className="animate-spin rounded-full h-12 w-12 border-2 border-slate-200 border-t-slate-900 mx-auto"></div>
                <p className="text-slate-600">
                  {isFromCache ? "Loading from cache..." : "Fetching candidates..."}
                </p>
                {error && (
                  <p className="text-sm text-red-600 mt-2">Error: {error}</p>
                )}
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-16">
              <div className="text-center space-y-4">
                <UserX className="h-16 w-16 text-red-300 mx-auto" />
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold text-red-900">Failed to Load Candidates</h3>
                  <p className="text-red-600 max-w-md mx-auto">{error}</p>
                  <Button onClick={refreshCandidates} variant="outline" className="mt-4">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Try Again
                  </Button>
                </div>
              </div>
            </div>
          ) : filteredCandidates.length === 0 ? (
            <div className="text-center py-16 space-y-4">
              <UserX className="h-16 w-16 text-slate-300 mx-auto" />
              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-slate-900">No Candidates Found</h3>
                <p className="text-slate-600">
                  {candidates.length === 0 
                    ? "No candidates have been analyzed yet. Please analyze some resumes first."
                    : "No candidates match your search criteria. Try adjusting your search."
                  }
                </p>
                {candidates.length === 0 && (
                  <div className="flex items-center justify-center gap-2 mt-4">
                    <Button onClick={refreshCandidates} variant="outline">
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Refresh
                    </Button>
                    <Button onClick={warmCache} variant="outline">
                      <Zap className="h-4 w-4 mr-2" />
                      Warm Cache
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredCandidates.map((candidate) => (
                <Card
                  key={candidate.id}
                  className="bg-white rounded-xl shadow-sm border border-slate-200 hover:shadow-md transition-all duration-200 cursor-pointer group"
                  onClick={() => onSelect(candidate)}
                >
                  <CardContent className="p-6">
                    <div className="space-y-4">
                      {/* Header */}
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center group-hover:bg-slate-200 transition-colors">
                            <User className="h-5 w-5 text-slate-600" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <h3 className="font-semibold text-slate-900 truncate">
                              {candidate.firstName} {candidate.lastName}
                            </h3>
                            <p className="text-sm text-slate-600 truncate">{candidate.position}</p>
                          </div>
                        </div>
                        {candidate.analysisScore && (
                          <div className="text-right">
                            <div className={cn("text-lg font-bold", getScoreColor(candidate.analysisScore))}>
                              {candidate.analysisScore}/10
                            </div>
                            <div className="text-xs text-slate-500">Score</div>
                          </div>
                        )}
                      </div>

                      {/* Enhanced Details */}
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <Calendar className="h-4 w-4" />
                          <span>Analyzed {candidate.lastAnalyzed?.toLocaleDateString()}</span>
                        </div>
                        
                        {candidate.verdict && (
                          <Badge 
                            variant="outline" 
                            className={cn("text-xs", getVerdictColor(candidate.verdict))}
                          >
                            {candidate.verdict.replace('_', ' ')}
                          </Badge>
                        )}

                        {candidate.interviewQuestions && (
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <MessageSquare className="h-4 w-4" />
                            <span>
                              {(candidate.interviewQuestions.technical?.length || 0) + 
                               (candidate.interviewQuestions.behavioral?.length || 0) + 
                               (candidate.interviewQuestions.cultural?.length || 0)} interview questions generated
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Enhanced Footer */}
        <div className="border-t border-slate-200 p-6 bg-slate-50">
          <div className="flex items-center justify-between text-sm text-slate-600">
            <span>
              {filteredCandidates.length} candidate{filteredCandidates.length !== 1 ? 's' : ''} available
              {isFromCache && ' (cached)'}
            </span>
            <span>Click a candidate to start interview assessment</span>
          </div>
        </div>
      </div>
    </div>
  );
} 